"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { xlncaContentEN } from "@/assets/dictionaries/en/xlnca-content";
import { xlncaContentES } from "@/assets/dictionaries/es/xlnca-content";

export default function XlncaInfo() {
  const [isSpanish, setIsSpanish] = useState(false);

  useEffect(() => {
    setIsSpanish(
      localStorage.getItem("selectedOption")?.toUpperCase() === "ES",
    );
  }, []);

  const content = isSpanish ? xlncaContentES : xlncaContentEN;

  return (
    <article
      lang={isSpanish ? "es" : "en"}
      className="space-y-12 py-16 text-xl leading-relaxed md:px-16 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gbci-primary"
    >
      <h1 className="mb-12">
        <Image
          src="/images/illustrations/service/xlnca.svg"
          alt="XLNC-A"
          width={550}
          height={110}
          unoptimized
          priority
          className="mx-auto h-auto w-3/4 max-w-[550px]"
        />
      </h1>

      <section aria-labelledby="xlnca-purpose">
        <h2 id="xlnca-purpose">{content.purpose.title}</h2>
        <p>{content.purpose.description}</p>
        <p className="my-6 font-bold">{content.purpose.highlight}</p>
        <p>{content.purpose.summary}</p>
      </section>

      <section aria-labelledby="xlnca-overview">
        <h2 id="xlnca-overview">{content.overview.title}</h2>
        {content.overview.paragraphs.map((paragraph) => (
          <p className="mb-6 last:mb-0" key={paragraph}>
            {paragraph}
          </p>
        ))}
      </section>

      <section aria-labelledby="xlnca-flow">
        <h2 id="xlnca-flow">{content.flow.title}</h2>
        <p>{content.flow.description}</p>
        <ol className="mt-8 grid gap-3 sm:grid-cols-5">
          {content.flow.steps.map((step, index) => (
            <li
              key={step}
              className="rounded-lg border border-gbci-primary/30 bg-gbci-primary/5 p-3 text-center text-base font-bold text-slate-800"
            >
              <span
                className="mb-2 block text-sm text-gbci-primary"
                aria-hidden="true"
              >
                0{index + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="xlnca-work">
        <h2 id="xlnca-work">{content.work.title}</h2>
        {content.work.paragraphs.map((paragraph) => (
          <p className="mb-6 last:mb-0" key={paragraph}>
            {paragraph}
          </p>
        ))}
      </section>

      <section aria-labelledby="xlnca-waste">
        <h2 id="xlnca-waste">{content.waste.title}</h2>
        <p className="mb-6">{content.waste.description}</p>
        <p>{content.waste.introduction}</p>
        <ul className="mt-6 list-disc space-y-3 pl-6 marker:text-gbci-primary">
          {content.waste.benefits.map((benefit) => (
            <li key={benefit}>{benefit}</li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="xlnca-adapt">
        <h2 id="xlnca-adapt">{content.adapt.title}</h2>
        <p>{content.adapt.description}</p>
      </section>

      <section
        aria-labelledby="xlnca-try"
        className="border-t border-slate-200 pt-10"
      >
        <h2 id="xlnca-try">{content.tryIt.title}</h2>
        <p>{content.tryIt.description}</p>
        <p className="mt-6 font-bold">{content.tryIt.closing}</p>
        <div className="mt-10 text-center">
          <Link
            href="/#contact"
            className="inline-block rounded-md bg-gradient-to-br from-gbci-primary to-gbci-accent p-4 font-bold text-slate-900 shadow-md transition-all hover:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gbci-primary"
          >
            {content.tryIt.contact}
          </Link>
        </div>
      </section>
    </article>
  );
}
